/**
 * Global error handler for production debugging
 * This helps track errors in production without exposing sensitive information
 */

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  timestamp: string;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorInfo[] = [];
  private readonly MAX_QUEUE_SIZE = 50;

  private constructor() {
    this.setupGlobalErrorHandlers();
  }

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private setupGlobalErrorHandlers() {
    if (typeof window === 'undefined') return;

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      this.logError({
        message: error?.message || 'Unhandled Promise Rejection',
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    });

    // Handle general errors
    window.addEventListener('error', (event) => {
      this.logError({
        message: event.message || 'Unknown Error',
        stack: event.error?.stack,
        url: event.filename || window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    });
  }

  public logError(errorInfo: ErrorInfo) {
    // Add to queue
    this.errorQueue.push(errorInfo);
    
    // Limit queue size
    if (this.errorQueue.length > this.MAX_QUEUE_SIZE) {
      this.errorQueue.shift();
    }

    // In production, you can send to error tracking service
    // For now, we'll just log to console with minimal info
    if (process.env.NODE_ENV === 'production') {
      // Log minimal error info for debugging
      // Format: [ERROR] message | url | timestamp
      const logMessage = `[ERROR] ${errorInfo.message} | ${errorInfo.url} | ${errorInfo.timestamp}`;
      
      // Only log if it's not a known browser extension error
      if (!this.isBrowserExtensionError(errorInfo.message)) {
        // In production, you might want to send to error tracking service
        // For now, we'll log to console (visible in browser DevTools)
      }
    } else {
      // In development, log full error details
      console.error('Error logged:', errorInfo);
    }
  }

  private isBrowserExtensionError(message: string): boolean {
    // Filter out known browser extension errors
    const extensionPatterns = [
      'onboarding.js',
      'content-script.js',
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
    ];

    return extensionPatterns.some((pattern) => message.includes(pattern));
  }

  public getErrorQueue(): ErrorInfo[] {
    return [...this.errorQueue];
  }

  public clearErrorQueue() {
    this.errorQueue = [];
  }
}

// Initialize error handler
if (typeof window !== 'undefined') {
  ErrorHandler.getInstance();
}
